import { Component, Host, Prop, h } from '@stencil/core';
import { getTenant } from '../../helpers/helpers';
import type { CssClassMap } from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-rating',
  styleUrl: 'cms-rating.scss',
  shadow: true,
})
export class CmsRating {
  /**
   * Set the rating value
   */
  @Prop() ratingValue: number;
  /**
   * Set the value how many users have voted
   */
  @Prop() ratingCount = 0;
  /**
   * Set the title text for this rating (placeholder {value}, {amount} )
   */
  @Prop() titleText: string;

  private ratingItems: Array<object> = [];
  private readonly maxRating = 5;
  private hasRatingColor = '#F99D27';
  private noRatingColor = '#cccccc';
  private readonly titlePlaceholder = '{value}';
  private readonly amountPlaceholder = '{amount}';

  connectedCallback() {
    if (getTenant() === 'bodenhaus') {
      this.hasRatingColor = '#F06900';
      this.noRatingColor = '#B3B3B3';
    }
  }

  componentWillLoad() {
    this.createRatingItems();
  }

  private getSvgClassMap(): CssClassMap {
    return {
      [`value-${this.ratingValue}`]: true,
    };
  }

  private getRatingsCount() {
    if (this.ratingCount !== undefined) {
      return <span class='rating-count-stars'>({this.ratingCount})</span>;
    }
  }

  private getSvgPath() {
    return 'M16.8 4c.4-1.3 2-1.3 2.4 0l2.5 8.4c.2.6.7 1 1.2 1l8.8-.3c1.3 0 1.8 1.6.8 2.3l-7.3 5c-.4.4-.6 1-.4 1.5l2.9 8.4c.4 1.2-1 2.2-2 1.4l-7-5.4c-.4-.3-1-.3-1.5 0l-7 5.4c-1 .8-2.3-.2-1.9-1.4l3-8.4c.1-.5 0-1.1-.5-1.4l-7.3-5c-1-.8-.5-2.4.8-2.4l8.8.2c.5 0 1-.3 1.2-.9z';
  }

  private renderRatingContent() {
    return (
      <div class={'ratings-container'}>
        {/* biome-ignore lint/a11y/noSvgWithoutTitle: biome doesn't recognize the provided title as prop */}
        <svg
          viewBox='0 0 180 36'
          height='18'
          class={this.getSvgClassMap()}
          xmlns='http://www.w3.org/2000/svg'
        >
          {this.titleText ? (
            <title>
              {this.titleText
                .replace(this.titlePlaceholder, `${this.ratingValue}`)
                .replace(this.amountPlaceholder, `${this.ratingCount}`)}
            </title>
          ) : (
            ''
          )}
          <defs>
            <path id='icon' d={this.getSvgPath()} />
          </defs>
          {this.ratingItems}
        </svg>
        {this.getRatingsCount()}
      </div>
    );
  }

  private createRatingItems() {
    const items = [];
    let tempResult = this.ratingValue * 100;
    let fillResult = 100;
    for (let i = 0; i < this.maxRating; i++) {
      if (tempResult < fillResult) {
        fillResult = tempResult;
      }
      if (tempResult < 0) {
        fillResult = 0;
      }
      items.push([
        <defs key={`def-${i}`}>
          <linearGradient id={`fill-${fillResult}`}>
            <stop offset='0%' stop-color={this.hasRatingColor} />
            <stop offset={`${fillResult}%`} stop-color={this.hasRatingColor} />
            <stop offset={`${fillResult}%`} stop-color={this.noRatingColor} />
            <stop offset='100%' stop-color={this.noRatingColor} />
          </linearGradient>
        </defs>,
        <use
          class={`icon-clone fill-${fillResult}`}
          id={`icon_${i}`}
          href='#icon'
          transform={`translate(${i * 36}, 0) scale(1)`}
          fill={`url(#fill-${fillResult}`}
          key={`use-${i}`}
        />,
      ]);
      tempResult -= 100;
    }
    this.ratingItems = items;
  }

  render() {
    return (
      <Host>
        {this.ratingValue > 0 && this.ratingValue !== undefined
          ? this.renderRatingContent()
          : ''}
      </Host>
    );
  }
}
